/**
 * Footer Component - Site footer with links and info
 * Provides navigation links, contact info, and legal information
 */

export class Footer {
  render(container: HTMLElement): void {
    container.innerHTML = `
      <footer class="footer" role="contentinfo">
        <div class="footer-content">
          <div class="footer-section">
            <h3 class="footer-title">🇸🇱 Sierra Leone Mpox Tracker</h3>
            <p class="footer-description">
              Real-time outbreak information to keep Sierra Leone safe and informed.
              Built for citizens, by the Ministry of Health.
            </p>
            <div class="footer-emergency">
              <strong>Emergency Hotline: <a href="tel:117">117</a></strong>
            </div>
          </div>
          
          <div class="footer-section">
            <h4 class="footer-heading">Quick Links</h4>
            <nav class="footer-nav">
              <a href="#symptoms" class="footer-link">🏥 Symptoms</a>
              <a href="#vaccines" class="footer-link">💉 Vaccines</a>
              <a href="#prevention" class="footer-link">🛡️ Prevention</a>
              <a href="#districts" class="footer-link">🗺️ Districts</a>
            </nav>
          </div>
          
          <div class="footer-section">
            <h4 class="footer-heading">Resources</h4>
            <nav class="footer-nav">
              <a href="https://www.who.int/emergencies/disease-outbreak-news/item/2022-DON385" 
                 class="footer-link" target="_blank" rel="noopener">
                WHO Information
              </a>
              <a href="https://www.cdc.gov/poxvirus/mpox/" 
                 class="footer-link" target="_blank" rel="noopener">
                CDC Guidelines
              </a>
              <a href="tel:+23222224236" class="footer-link">📞 Ministry of Health</a>
              <a href="mailto:info@health.gov.sl" class="footer-link">✉️ Contact Us</a>
            </nav>
          </div>
          
          <div class="footer-section">
            <h4 class="footer-heading">Data & Technology</h4>
            <div class="footer-tech">
              <p class="data-update">
                Data updated every 30 minutes from health facilities across Sierra Leone
              </p>
              <div class="tech-stack">
                <span class="tech-badge">📱 Mobile-First</span>
                <span class="tech-badge">⚡ Fast Loading</span>
                <span class="tech-badge">🔄 Offline Ready</span>
                <span class="tech-badge">♿ Accessible</span>
              </div>
            </div>
          </div>
        </div>
        
        <div class="footer-bottom">
          <div class="footer-bottom-content">
            <div class="footer-legal">
              <p>
                © 2025 Republic of Sierra Leone - Ministry of Health and Sanitation
              </p>
              <div class="footer-links">
                <a href="#privacy" class="footer-link-small">Privacy Policy</a>
                <a href="#terms" class="footer-link-small">Terms of Use</a>
                <a href="#accessibility" class="footer-link-small">Accessibility</a>
                <a href="#open-source" class="footer-link-small">Open Source</a>
              </div>
            </div>
            
            <div class="footer-social">
              <span class="social-label">Follow Updates:</span>
              <div class="social-links">
                <a href="#" class="social-link" aria-label="Twitter">🐦</a>
                <a href="#" class="social-link" aria-label="Facebook">📘</a>
                <a href="#" class="social-link" aria-label="WhatsApp">💬</a>
              </div>
            </div>
          </div>
          
          <div class="footer-disclaimer">
            <p>
              <strong>Disclaimer:</strong> This information is for educational purposes. 
              For medical emergencies, call 117 immediately. Always consult healthcare 
              professionals for medical advice.
            </p>
          </div>
        </div>
      </footer>
    `;

    this.setupEventListeners();
    this.addStyles();
  }

  private setupEventListeners(): void {
    // Handle internal navigation
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      
      if (target.tagName === 'A' && target.getAttribute('href')?.startsWith('#')) {
        event.preventDefault();
        const section = target.getAttribute('href')?.substring(1);
        this.handleInternalNavigation(section || '');
      }
    });
  }

  private handleInternalNavigation(section: string): void {
    switch (section) {
      case 'symptoms':
        this.showSymptomsInfo();
        break;
      case 'vaccines':
        this.showVaccineInfo();
        break;
      case 'prevention':
        this.showPreventionInfo();
        break;
      case 'districts':
        this.scrollToDistricts();
        break;
      case 'privacy':
        this.showPrivacyPolicy();
        break;
      case 'terms':
        this.showTermsOfUse();
        break;
      case 'accessibility':
        this.showAccessibilityInfo();
        break;
      case 'open-source':
        this.showOpenSourceInfo();
        break;
    }
  }

  private showSymptomsInfo(): void {
    const modal = this.createModal(
      '🏥 Mpox Symptoms',
      `
        <div class="symptoms-guide">
          <h3>What to Watch For</h3>
          <p>
            Mpox symptoms typically appear 6-13 days after exposure but can range from 5-21 days.
          </p>
          
          <div class="symptoms-section">
            <h4>🌡️ Early Symptoms (1-3 days)</h4>
            <ul>
              <li>Fever (often the first symptom)</li>
              <li>Intense headache</li>
              <li>Muscle aches and back pain</li>
              <li>Exhaustion and fatigue</li>
              <li>Swollen lymph nodes (distinguishes from smallpox)</li>
            </ul>
          </div>
          
          <div class="symptoms-section">
            <h4>🔴 Rash Development (1-4 days after fever)</h4>
            <ul>
              <li>Starts on face and spreads to rest of body</li>
              <li>Progresses through stages: spots → bumps → blisters → scabs</li>
              <li>Rash can appear on palms and soles</li>
              <li>May appear in mouth, genitals, or eyes</li>
              <li>Usually 10-150 lesions</li>
            </ul>
          </div>
          
          <div class="symptoms-section">
            <h4>⚠️ Severe Symptoms (Seek immediate care)</h4>
            <ul>
              <li>Difficulty breathing or chest pain</li>
              <li>Signs of infection in rash lesions</li>
              <li>Severe dehydration</li>
              <li>Confusion or difficulty thinking clearly</li>
              <li>Persistent high fever over 3 days</li>
            </ul>
          </div>
          
          <div class="alert alert-warning">
            <strong>Important:</strong> Symptoms can vary between individuals. 
            Some people may have mild symptoms while others become severely ill.
          </div>
          
          <div class="alert alert-danger">
            <strong>Emergency Action:</strong> If you have symptoms, isolate immediately 
            and call 117 for guidance. Do not visit health facilities without calling first.
          </div>
        </div>
      `
    );
    this.showModal(modal);
  }

  private showVaccineInfo(): void {
    const modal = this.createModal(
      '💉 Mpox Vaccination Information',
      `
        <div class="vaccine-guide">
          <h3>Vaccine Availability in Sierra Leone</h3>
          <p>
            The mpox vaccine is available in Sierra Leone through the Ministry of Health 
            and international health partners.
          </p>
          
          <div class="vaccine-section">
            <h4>💉 Who Should Get Vaccinated?</h4>
            <ul>
              <li><strong>High Priority:</strong> Healthcare workers treating mpox patients</li>
              <li><strong>High Priority:</strong> Laboratory staff handling specimens</li>
              <li><strong>Priority:</strong> Close contacts of confirmed cases</li>
              <li><strong>Priority:</strong> People at increased risk of exposure</li>
              <li><strong>General Public:</strong> Based on availability and risk assessment</li>
            </ul>
          </div>
          
          <div class="vaccine-section">
            <h4>🏥 Where to Get Vaccinated</h4>
            <ul>
              <li>Government hospitals in all 16 districts</li>
              <li>Designated health centers (check district listings)</li>
              <li>Mobile vaccination teams in remote areas</li>
              <li>Special clinics during outbreaks</li>
            </ul>
          </div>
          
          <div class="vaccine-section">
            <h4>📋 Vaccination Process</h4>
            <ul>
              <li>Free of charge for all Sierra Leone residents</li>
              <li>Two doses given 4 weeks apart</li>
              <li>Brings national ID or other identification</li>
              <li>Brief health screening before vaccination</li>
              <li>15-minute observation period after vaccination</li>
            </ul>
          </div>
          
          <div class="vaccine-section">
            <h4>⚡ Post-Vaccination</h4>
            <ul>
              <li>Continue prevention measures even after vaccination</li>
              <li>Mild side effects: soreness, redness at injection site</li>
              <li>Rare side effects: fever, headache, fatigue</li>
              <li>Protection begins 2 weeks after final dose</li>
            </ul>
          </div>
          
          <div class="alert alert-info">
            <strong>Vaccine Schedule:</strong> Check with your local health facility 
            for current availability and appointment scheduling.
          </div>
          
          <div class="contact-info">
            <p>
              <strong>For Vaccination Information:</strong><br>
              📞 Call 117 (Emergency Hotline)<br>
              🏥 Visit your nearest government health facility<br>
              📧 Email: vaccines@health.gov.sl
            </p>
          </div>
        </div>
      `
    );
    this.showModal(modal);
  }

  private showPreventionInfo(): void {
    const modal = this.createModal(
      '🛡️ Prevention Guidelines',
      `
        <div class="prevention-guide">
          <h3>How to Protect Yourself and Others</h3>
          
          <div class="prevention-section">
            <h4>🧼 Personal Hygiene</h4>
            <ul>
              <li>Wash hands frequently with soap and water for at least 20 seconds</li>
              <li>Use alcohol-based hand sanitizer when soap is not available</li>
              <li>Avoid touching your face, eyes, nose, and mouth</li>
              <li>Cover coughs and sneezes with your elbow or tissue</li>
            </ul>
          </div>
          
          <div class="prevention-section">
            <h4>🏠 At Home</h4>
            <ul>
              <li>Clean and disinfect frequently touched surfaces</li>
              <li>Wash clothes and bedding in hot water</li>
              <li>Isolate if you have symptoms</li>
              <li>Keep good ventilation in living spaces</li>
            </ul>
          </div>
          
          <div class="prevention-section">
            <h4>👥 Social Measures</h4>
            <ul>
              <li>Avoid close contact with people who are sick</li>
              <li>Stay home if you feel unwell</li>
              <li>Avoid sharing personal items</li>
              <li>Practice physical distancing when possible</li>
            </ul>
          </div>
          
          <div class="prevention-section">
            <h4>💉 Vaccination</h4>
            <ul>
              <li>Get vaccinated if you are eligible</li>
              <li>Follow vaccination schedule recommendations</li>
              <li>Continue protective measures even after vaccination</li>
            </ul>
          </div>
          
          <div class="alert alert-info">
            <strong>Remember:</strong> Prevention is your best protection. 
            These simple steps can significantly reduce your risk of infection.
          </div>
        </div>
      `
    );
    this.showModal(modal);
  }

  private scrollToDistricts(): void {
    const modal = this.createModal(
      '🗺️ Sierra Leone Districts',
      `
        <div class="districts-guide">
          <h3>16 Districts of Sierra Leone</h3>
          <p>
            Sierra Leone is divided into 16 districts across 5 regions. Each district 
            has dedicated health facilities monitoring and responding to mpox cases.
          </p>
          
          <div class="districts-section">
            <h4>🏛️ Western Area</h4>
            <ul>
              <li><strong>Western Area Urban</strong> - Freetown (Capital)</li>
              <li><strong>Western Area Rural</strong> - Waterloo, Hastings</li>
            </ul>
          </div>
          
          <div class="districts-section">
            <h4>🌾 Northern Province</h4>
            <ul>
              <li><strong>Bombali</strong> - Makeni</li>
              <li><strong>Falaba</strong> - Bendugu</li>
              <li><strong>Koinadugu</strong> - Kabala</li>
              <li><strong>Kambia</strong> - Kambia</li>
              <li><strong>Karene</strong> - Kamakwie</li>
              <li><strong>Port Loko</strong> - Port Loko</li>
            </ul>
          </div>
          
          <div class="districts-section">
            <h4>🏔️ North West Province</h4>
            <ul>
              <li><strong>Koinadugu</strong> - Kabala</li>
            </ul>
          </div>
          
          <div class="districts-section">
            <h4>🌳 Southern Province</h4>
            <ul>
              <li><strong>Bo</strong> - Bo (Second largest city)</li>
              <li><strong>Bonthe</strong> - Mattru Jong</li>
              <li><strong>Moyamba</strong> - Moyamba</li>
              <li><strong>Pujehun</strong> - Pujehun</li>
            </ul>
          </div>
          
          <div class="districts-section">
            <h4>⛰️ Eastern Province</h4>
            <ul>
              <li><strong>Kailahun</strong> - Kailahun</li>
              <li><strong>Kenema</strong> - Kenema</li>
              <li><strong>Kono</strong> - Koidu</li>
            </ul>
          </div>
          
          <div class="alert alert-info">
            <strong>Health Services:</strong> Each district has a District Health Management Team (DHMT) 
            coordinating mpox surveillance and response activities.
          </div>
          
          <div class="districts-section">
            <h4>🏥 District Health Facilities</h4>
            <ul>
              <li>Government hospitals in each district headquarters</li>
              <li>Community Health Centers (CHCs) in chiefdoms</li>
              <li>Community Health Posts (CHPs) in villages</li>
              <li>Mobile health teams for remote areas</li>
            </ul>
          </div>
          
          <div class="contact-info">
            <p>
              <strong>Find Your District Services:</strong><br>
              📞 Call 117 for district-specific information<br>
              🏥 Contact your nearest health facility<br>
              📧 Email: districts@health.gov.sl
            </p>
          </div>
        </div>
      `
    );
    this.showModal(modal);
  }

  private showPrivacyPolicy(): void {
    const modal = this.createModal(
      '🔒 Privacy Policy',
      `
        <div class="legal-document">
          <h3>Your Privacy Matters</h3>
          <p>
            The Sierra Leone Mpox Tracker is committed to protecting your privacy 
            and ensuring transparent use of data.
          </p>
          
          <h4>Data Collection</h4>
          <ul>
            <li>We do not collect personal information</li>
            <li>No registration or login required</li>
            <li>Usage analytics are anonymized</li>
            <li>Location data is optional and not stored</li>
          </ul>
          
          <h4>Data Storage</h4>
          <ul>
            <li>Health statistics are public information</li>
            <li>Data is cached locally for offline access</li>
            <li>No personal health information is stored</li>
            <li>You can clear local data anytime</li>
          </ul>
          
          <h4>Data Sharing</h4>
          <ul>
            <li>Health data comes from official government sources</li>
            <li>No data is shared with third parties</li>
            <li>All data follows WHO privacy guidelines</li>
          </ul>
          
          <h4>Your Rights</h4>
          <ul>
            <li>Right to access public health information</li>
            <li>Right to delete local app data</li>
            <li>Right to contact us with questions</li>
          </ul>
          
          <p>
            <strong>Contact:</strong> For privacy questions, email 
            <a href="mailto:privacy@health.gov.sl">privacy@health.gov.sl</a>
          </p>
        </div>
      `
    );
    this.showModal(modal);
  }

  private showTermsOfUse(): void {
    const modal = this.createModal(
      '📋 Terms of Use',
      `
        <div class="legal-document">
          <h3>Terms of Service</h3>
          <p>
            By using the Sierra Leone Mpox Tracker, you agree to these terms.
          </p>
          
          <h4>Intended Use</h4>
          <ul>
            <li>For informational and educational purposes only</li>
            <li>Not a substitute for professional medical advice</li>
            <li>Emergency situations require immediate medical attention</li>
            <li>Always consult healthcare professionals for health decisions</li>
          </ul>
          
          <h4>Data Accuracy</h4>
          <ul>
            <li>Data is updated regularly from official sources</li>
            <li>We strive for accuracy but cannot guarantee completeness</li>
            <li>Information may be delayed or revised</li>
            <li>Official health authorities are the ultimate source</li>
          </ul>
          
          <h4>Acceptable Use</h4>
          <ul>
            <li>Use responsibly and ethically</li>
            <li>Do not attempt to disrupt the service</li>
            <li>Respect intellectual property rights</li>
            <li>Report issues or errors to help improve the service</li>
          </ul>
          
          <h4>Limitations</h4>
          <ul>
            <li>Service provided "as is" without warranties</li>
            <li>Government reserves right to modify or discontinue</li>
            <li>Not liable for decisions based on this information</li>
          </ul>
          
          <div class="alert alert-warning">
            <strong>Medical Emergency:</strong> For immediate medical help, 
            call 117 or visit your nearest health facility.
          </div>
        </div>
      `
    );
    this.showModal(modal);
  }

  private showAccessibilityInfo(): void {
    const modal = this.createModal(
      '♿ Accessibility Statement',
      `
        <div class="accessibility-info">
          <h3>Accessibility Commitment</h3>
          <p>
            We are committed to ensuring digital accessibility for people with disabilities.
          </p>
          
          <h4>Standards Compliance</h4>
          <ul>
            <li>WCAG 2.1 AA compliance</li>
            <li>Keyboard navigation support</li>
            <li>Screen reader compatibility</li>
            <li>High contrast mode support</li>
          </ul>
          
          <h4>Accessibility Features</h4>
          <ul>
            <li>📱 Works on all devices and screen sizes</li>
            <li>⌨️ Full keyboard navigation</li>
            <li>🔊 Screen reader announcements</li>
            <li>🎨 High contrast color schemes</li>
            <li>📖 Clear, simple language</li>
            <li>⚡ Fast loading on slow connections</li>
          </ul>
          
          <h4>Alternative Access</h4>
          <ul>
            <li>📞 Phone: Call 117 for emergency information</li>
            <li>💬 WhatsApp: Send message to [number]</li>
            <li>📧 Email: info@health.gov.sl</li>
            <li>🏥 In person: Visit any health facility</li>
          </ul>
          
          <h4>Feedback</h4>
          <p>
            Help us improve accessibility. Report issues or suggestions to 
            <a href="mailto:accessibility@health.gov.sl">accessibility@health.gov.sl</a>
          </p>
          
          <h4>Assistive Technology Tested</h4>
          <ul>
            <li>NVDA screen reader</li>
            <li>JAWS screen reader</li>
            <li>VoiceOver (iOS/macOS)</li>
            <li>TalkBack (Android)</li>
          </ul>
        </div>
      `
    );
    this.showModal(modal);
  }

  private showOpenSourceInfo(): void {
    const modal = this.createModal(
      '🔓 Open Source',
      `
        <div class="open-source-info">
          <h3>Open Source Commitment</h3>
          <p>
            This tracker is built with transparency and community collaboration in mind.
          </p>
          
          <h4>Open Source Benefits</h4>
          <ul>
            <li>🔍 Transparent and auditable code</li>
            <li>🤝 Community contributions welcome</li>
            <li>🛡️ Enhanced security through peer review</li>
            <li>🌍 Reusable for other health emergencies</li>
          </ul>
          
          <h4>Technologies Used</h4>
          <ul>
            <li>⚡ Vite - Fast build tool</li>
            <li>🗺️ Leaflet - Interactive maps</li>
            <li>📊 Chart.js - Data visualization</li>
            <li>☁️ Cloudflare Workers - Edge computing</li>
            <li>💾 Cloudflare KV - Data storage</li>
          </ul>
          
          <h4>Contributing</h4>
          <p>
            Developers, designers, and health professionals can contribute to 
            improving this system for Sierra Leone and other countries.
          </p>
          
          <h4>License</h4>
          <p>
            Released under MIT License - free to use, modify, and distribute 
            for public health purposes.
          </p>
          
          <div class="contact-info">
            <p>
              <strong>Technical Contact:</strong><br>
              Email: <a href="mailto:tech@health.gov.sl">tech@health.gov.sl</a><br>
              GitHub: [Repository link when available]
            </p>
          </div>
        </div>
      `
    );
    this.showModal(modal);
  }

  private createModal(title: string, content: string): HTMLElement {
    const modal = document.createElement('div');
    modal.className = 'modal-overlay';
    modal.innerHTML = `
      <div class="modal-content">
        <div class="modal-header">
          <h2>${title}</h2>
          <button class="modal-close" aria-label="Close modal">×</button>
        </div>
        <div class="modal-body">
          ${content}
        </div>
        <div class="modal-footer">
          <button class="btn btn-primary modal-close">Close</button>
        </div>
      </div>
    `;
    return modal;
  }

  private showModal(modal: HTMLElement): void {
    document.body.appendChild(modal);

    // Setup close handlers
    const closeButtons = modal.querySelectorAll('.modal-close');
    const closeModal = () => {
      modal.remove();
      document.body.style.overflow = '';
    };

    closeButtons.forEach(btn => {
      btn.addEventListener('click', closeModal);
    });

    modal.addEventListener('click', (e) => {
      if (e.target === modal) closeModal();
    });

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Focus management
    const firstButton = modal.querySelector('button');
    firstButton?.focus();
  }

  private addStyles(): void {
    const style = document.createElement('style');
    style.textContent = `
      /* Modal content styles */
      .prevention-guide,
      .symptoms-guide,
      .vaccine-guide,
      .districts-guide,
      .legal-document,
      .accessibility-info,
      .open-source-info {
        max-height: 70vh;
        overflow-y: auto;
      }
      
      .prevention-section,
      .symptoms-section,
      .vaccine-section,
      .districts-section {
        margin-bottom: var(--space-6);
      }
      
      .prevention-section h4,
      .symptoms-section h4,
      .vaccine-section h4,
      .districts-section h4 {
        color: var(--primary-color);
        margin-bottom: var(--space-3);
      }
      
      .prevention-section ul,
      .symptoms-section ul,
      .vaccine-section ul,
      .districts-section ul {
        margin-left: var(--space-4);
      }
      
      .prevention-section li,
      .symptoms-section li,
      .vaccine-section li,
      .districts-section li {
        margin-bottom: var(--space-1);
        line-height: 1.5;
      }
      
      .alert {
        padding: var(--space-4);
        border-radius: var(--radius);
        margin: var(--space-4) 0;
      }
      
      .alert-info {
        background: rgba(59, 130, 246, 0.1);
        border: 1px solid #3b82f6;
        color: #1e40af;
      }
      
      .alert-warning {
        background: rgba(245, 158, 11, 0.1);
        border: 1px solid #f59e0b;
        color: #92400e;
      }
      
      .contact-info {
        background: var(--gray-50);
        padding: var(--space-4);
        border-radius: var(--radius);
        margin-top: var(--space-4);
      }
    `;
    
    document.head.appendChild(style);
  }
}